import type { CombinationExperimentInfo } from 'statisticsdb/types';
import { IconAlertTriangle } from '@tabler/icons-react';

/**
 * Shown when the selected experiments were never randomising at the same time.
 *
 * The common cause is deployment rather than dates. Experiments 5417 and 5473 looked like a
 * six-day overlap on their start and end dates, but 5417's variant was deployed a month
 * before 5473 began, so no visitor was ever randomly assigned in both at once and there is no
 * interaction to measure. Naming which experiment stopped randomising, and when, is more
 * useful than an empty table.
 */
type NoWindowNoticeProps = { experiments: CombinationExperimentInfo[] };

const day = (ms: number) =>
  new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

export const NoWindowNotice = ({ experiments }: NoWindowNoticeProps) => (
  <section className="bg-panel border-warn-line rounded-panel flex gap-3 border p-3" data-tour="combination-no-window">
    <IconAlertTriangle className="text-warn mt-0.5 size-4 shrink-0" />
    <div className="space-y-2">
      <p className="text-warn text-body font-medium">These experiments never randomised at the same time</p>
      <p className="supporting max-w-3xl">
        A combination only means something while both experiments were still splitting traffic. Once a variant is
        deployed everyone receives it, so the experiment stops randomising even though it keeps being served, and there
        is no comparison left to make.
      </p>
      <div className="space-y-1 pt-1">
        {experiments.map((exp) => (
          <p key={exp.id} className="supporting">
            <span className="text-foreground font-medium">{exp.name}</span>{' '}
            {exp.randomisingWindow
              ? `randomised ${day(exp.randomisingWindow.from)} to ${day(exp.randomisingWindow.to)}`
              : 'never randomised: it has no start date, or a variant was deployed before it began'}
            {exp.randomisingWindow && exp.variants.some((v) => v.deployed) ? ' (ends at deployment)' : ''}
          </p>
        ))}
      </div>
    </div>
  </section>
);
